import { OptionsProduction } from "hast-util-to-jsx-runtime/lib/types";
import { type Options as RemarkRehypeOptions } from "mdast-util-to-hast";
import { useMemo } from "react";
import * as jsxRuntime from "react/jsx-runtime";
import rehypeReact from "rehype-react";
import rehypeSanitize from "rehype-sanitize";
import remarkParse, { type Options as RemarkParseOptions } from "remark-parse";
import remarkToRehype from "remark-rehype";
import { unified, type PluggableList } from "unified";

export interface MarkdownOptions {
  /** Optional error handler callback for processing failures */
  onError?: (err: Error) => void;
  /** Rehype plugins for HTML AST transformation */
  rehypePlugins?: PluggableList;
  /** Configuration options for rehype-react, including custom component mapping */
  rehypeReactOptions?: Partial<OptionsProduction>;
  /** Configuration options for the initial markdown parsing */
  remarkParseOptions?: RemarkParseOptions;
  /** Remark plugins for markdown AST transformation */
  remarkPlugins?: PluggableList;
  /** Options for converting from mdast to hast */
  remarkToRehypeOptions?: RemarkRehypeOptions;
}

interface Props {
  children: string;
  className?: string;
  options?: MarkdownOptions;
}

/**
 * Renders markdown source text to HTML using remark -> mdast (ir) -> rehype -> hast (ir) -> react
 * Resulting DOM is sanitized with rehypeSanitize
 */
export function Markdown({ children, options = {} }: Props) {
  const {
    onError = console.error,
    rehypePlugins = [],
    rehypeReactOptions,
    remarkParseOptions,
    remarkPlugins = [],
    remarkToRehypeOptions
  } = options;

  const content = useMemo(() => {
    try {
      const processor = unified()
        .use(remarkParse, remarkParseOptions)
        .use(remarkPlugins)
        .use(remarkToRehype, remarkToRehypeOptions)
        .use(rehypeSanitize)
        .use(rehypePlugins)
        .use(rehypeReact, {
          ...rehypeReactOptions,
          Fragment: jsxRuntime.Fragment,
          jsx: jsxRuntime.jsx,
          jsxs: jsxRuntime.jsxs
        });

      const res = processor.processSync(children);
      return res.result;
    } catch (error) {
      onError(error as Error);
      return null;
    }
  }, [children, onError, rehypePlugins, rehypeReactOptions, remarkParseOptions, remarkPlugins, remarkToRehypeOptions]);

  return content;
}
